import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { FileText, Download, Trash2 } from 'lucide-react';
import { useAuth } from '@/auth';
import type { EscrituraAdicional } from '../types';

interface EscrituraAdicionalItemProps {
  escritura: EscrituraAdicional;
  canUploadEscritura: boolean;
  onDelete: (escritura: EscrituraAdicional) => Promise<void>;
  onDownload: (escritura: EscrituraAdicional) => Promise<void>;
}

export const EscrituraAdicionalItem: React.FC<EscrituraAdicionalItemProps> = ({
  escritura,
  canUploadEscritura,
  onDelete,
  onDownload,
}) => {
  const { user } = useAuth();
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const canDelete = canUploadEscritura || user?.id === escritura.uploadedBy.id;

  const handleDeleteConfirm = async () => {
    setDeleting(true);
    try {
      await onDelete(escritura);
    } catch {
      // error toast handled by parent
    } finally {
      setDeleting(false);
      setConfirmingDelete(false);
    }
  };

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="p-4">
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 flex items-center justify-center bg-red-50 rounded flex-shrink-0">
            <FileText className="w-5 h-5 text-red-600" />
          </div>

          <div className="flex-1 min-w-0">
            <h4 className="font-medium text-gray-900 truncate" title={escritura.descripcion}>
              {escritura.descripcion}
            </h4>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-sm text-gray-500">
              <span>{escritura.formattedSize}</span>
              <span>•</span>
              <span className="truncate">{escritura.fileName}</span>
              <span>•</span>
              <span>{new Date(escritura.createdAt).toLocaleDateString('es-AR')}</span>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            {confirmingDelete ? (
              <>
                <span className="text-xs text-red-600 font-medium whitespace-nowrap">¿Eliminar?</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setConfirmingDelete(false)}
                  disabled={deleting}
                  className="h-7 px-2 text-xs text-gray-600 hover:bg-gray-100"
                >
                  No
                </Button>
                <Button
                  size="sm"
                  onClick={handleDeleteConfirm}
                  disabled={deleting}
                  className="h-7 px-2 text-xs bg-red-600 hover:bg-red-700 text-white"
                >
                  {deleting ? '...' : 'Sí'}
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onDownload(escritura)}
                  title="Descargar"
                  className="h-8 w-8 p-0"
                >
                  <Download className="w-4 h-4 text-green-600" />
                </Button>

                {canDelete && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setConfirmingDelete(true)}
                    title="Eliminar"
                    className="h-8 w-8 p-0"
                  >
                    <Trash2 className="w-4 h-4 text-red-600" />
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
